<template>
  <div
    class="sidebar-user-profile"
    :class="{ 'collapsed': isCollapsed }"
  >
    <div class="user-profile-container">
      <!-- Avatar e Informações do Usuário (Clicável) -->
      <b-dropdown
        ref="userDropdown"
        variant="link"
        toggle-class="p-0 user-dropdown-toggle"
        no-caret
        right
        class="sidebar-footer-user-dropdown"
        :class="{ 'w-100': isCollapsed }"
        menu-class="user-dropdown-menu"
      >
        <template #button-content>
          <div class="user-info-wrapper">
            <b-avatar
              :src="userData.avatar"
              size="40"
              variant="light-primary"
              class="user-avatar"
            >
              <feather-icon
                v-if="!userData.avatar"
                icon="UserIcon"
                size="18"
              />
            </b-avatar>

            <div
              v-if="!isCollapsed"
              class="user-details"
            >
              <p class="user-name mb-0">
                {{ displayUserName }}
              </p>
              <span class="user-role">{{ userData.role }}</span>
            </div>

            <feather-icon
              v-if="!isCollapsed"
              icon="ChevronUpIcon"
              size="16"
              class="chevron-icon"
            />
          </div>
        </template>

        <b-dropdown-item @click="logout">
          <feather-icon
            icon="LogOutIcon"
            size="16"
            class="mr-50"
          />
          <span>Sair</span>
        </b-dropdown-item>
      </b-dropdown>

      <!-- Botão de Notificações -->
      <div
        v-if="!isCollapsed"
        class="notification-toggle-wrapper"
      >
        <b-button
          variant="flat-secondary"
          size="sm"
          class="btn-icon notification-toggle-btn"
          @click="openNotificationsModal"
        >
          <feather-icon
            icon="BellIcon"
            size="18"
          />
          <span
            v-if="unreadCount > 0"
            class="notification-badge"
          >{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
        </b-button>
      </div>

      <!-- Botão de Toggle de Tema -->
      <div
        v-if="!isCollapsed"
        class="theme-toggle-wrapper"
      >
        <b-button
          variant="flat-secondary"
          size="sm"
          class="btn-icon theme-toggle-btn"
          @click="toggleTheme"
        >
          <feather-icon
            :icon="isDark ? 'SunIcon' : 'MoonIcon'"
            size="18"
          />
        </b-button>
      </div>

      <!-- Quando colapsado, mostrar botões empilhados -->
      <div
        v-if="isCollapsed"
        class="theme-toggle-collapsed"
      >
        <b-button
          variant="flat-secondary"
          size="sm"
          class="btn-icon notification-toggle-btn mb-50"
          @click="openNotificationsModal"
        >
          <feather-icon
            icon="BellIcon"
            size="18"
          />
          <span
            v-if="unreadCount > 0"
            class="notification-badge"
          >{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
        </b-button>
        <b-button
          variant="flat-secondary"
          size="sm"
          class="btn-icon theme-toggle-btn"
          @click="toggleTheme"
        >
          <feather-icon
            :icon="isDark ? 'SunIcon' : 'MoonIcon'"
            size="18"
          />
        </b-button>
      </div>
    </div>

    <!-- Alerta de instabilidade (Meta): modais removidos; lógica comentada no <script> para reativar. -->

    <!-- Modal de Notificações -->
    <b-modal
      v-model="showNotificationsModal"
      title="Novidades e Atualizações"
      size="lg"
      centered
      hide-footer
      modal-class="notifications-modal"
    >
      <div class="notifications-container">
        <div
          v-if="notifications.length === 0"
          class="text-center py-4"
        >
          <feather-icon
            icon="BellOffIcon"
            size="48"
            class="text-muted mb-2"
          />
          <p class="text-muted mb-0">
            Nenhuma notificação no momento
          </p>
        </div>

        <div
          v-for="(notification, index) in notifications"
          :key="notification.id"
          class="notification-item"
          :class="{ 'unread': !notification.read }"
          @click="markAsRead(notification)"
        >
          <div class="notification-icon">
            <b-avatar
              :variant="notification.variant || 'light-primary'"
              size="42"
            >
              <feather-icon
                :icon="notification.icon || 'InfoIcon'"
                size="18"
              />
            </b-avatar>
          </div>
          <div class="notification-content">
            <div class="notification-header">
              <h6 class="notification-title mb-0">
                {{ notification.title }}
              </h6>
              <small class="notification-date text-muted">{{ formatDate(notification.date) }}</small>
            </div>
            <p class="notification-text mb-0">
              {{ notification.message }}
            </p>
            <div
              v-if="notification.details"
              class="notification-details mt-2"
            >
              <ul class="mb-0 pl-3">
                <li
                  v-for="(detail, i) in notification.details"
                  :key="i"
                  class="text-muted small"
                >{{ detail }}</li>
              </ul>
            </div>
            <b-badge
              v-if="notification.badge"
              :variant="notification.badgeVariant || 'primary'"
              class="mt-2"
            >{{ notification.badge }}</b-badge>
          </div>
        </div>
      </div>

      <div
        v-if="notifications.length > 0"
        class="notifications-footer mt-3 pt-3 border-top"
      >
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="markAllAsRead"
        >
          <feather-icon
            icon="CheckCircleIcon"
            size="14"
            class="mr-50"
          />
          Marcar todas como lidas
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAvatar, BButton, BDropdown, BDropdownItem, BDropdownDivider, BModal, BBadge,
} from 'bootstrap-vue'
import {
  computed, watch, ref, onMounted,
} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { initialAbility } from '@/libs/acl/config'
import useJwt from '@/auth/jwt/useJwt'
import { formatUserDisplayNameCompact } from '@/auth/utils'

const NOTIFICATIONS_STORAGE_KEY = 'manyconvert_notifications_read'
// const INSTABILITY_ALERT_KEY = 'manyconvert_instability_alert_2026_03_26'

const PLATFORM_NOTIFICATIONS = [
  {
    id: 'alerta-2026-03-26-meta',
    title: '⚠️ Instabilidade na API da Meta',
    message: 'A Meta está enfrentando problemas no sistema de pagamento, o que pode causar falhas em alguns envios de campanhas, notificações, envio de template e criação de uma nova conversa.',
    icon: 'AlertTriangleIcon',
    variant: 'light-danger',
    date: '2026-03-26',
    badge: 'Importante',
    badgeVariant: 'danger',
    details: [
      'Alguns envios de campanhas podem falhar temporariamente',
      'Notificações transacionais podem ser afetadas',
      'Estamos monitorando e os envios serão normalizados assim que a Meta resolver',
    ],
  },
  {
    id: 'update-2026-03-26',
    title: 'Novos Relatórios e Visão Geral',
    message: 'Adicionamos novo relatório de recomendações e visão geral dos atendimentos para acompanhar melhor seus resultados.',
    icon: 'BarChart2Icon',
    variant: 'light-success',
    date: '2026-03-26',
    badge: 'Novo',
    badgeVariant: 'success',
    details: [
      'Relatório completo de recomendações de produtos',
      'Visão geral dos atendimentos no helper',
      'Melhorias no sidebar e navegação',
    ],
  },
  {
    id: 'update-2026-03-25',
    title: 'Menu Reorganizado',
    message: 'O menu lateral foi reorganizado para facilitar a navegação entre as funcionalidades.',
    icon: 'LayoutIcon',
    variant: 'light-info',
    date: '2026-03-25',
    details: [
      'Nova disposição dos itens do menu',
      'Sidebar agora salva sua preferência (expandido/recolhido)',
      'Interface mais limpa e intuitiva',
    ],
  },
  {
    id: 'update-2026-03-24',
    title: 'Melhorias nas Campanhas',
    message: 'Atualizamos a criação de campanhas com novos recursos e aviso de cobrança no envio de teste.',
    icon: 'SendIcon',
    variant: 'light-warning',
    date: '2026-03-24',
    details: [
      'Aviso de cobrança antes do envio de teste',
      'Paginação de campanhas melhorada',
      'Ajustes visuais nos cards e botões',
    ],
  },
  {
    id: 'update-2026-03-20',
    title: 'Relatório de Campanhas Segmentadas',
    message: 'Novo relatório completo para acompanhar o desempenho das suas campanhas segmentadas.',
    icon: 'PieChartIcon',
    variant: 'light-primary',
    date: '2026-03-20',
    details: [
      'Métricas detalhadas por campanha',
      'Filtros por período personalizáveis',
      'Nova tela base para gerenciamento de planos',
    ],
  },
  {
    id: 'update-2026-03-19',
    title: 'Criação de Campanhas Melhorada',
    message: 'O processo de criação de campanhas agora é step-by-step, mais intuitivo e com preview do WhatsApp.',
    icon: 'CheckSquareIcon',
    variant: 'light-success',
    date: '2026-03-19',
    details: [
      'Criação passo a passo simplificada',
      'Preview do WhatsApp em tela cheia',
      'Botões e títulos mais claros',
    ],
  },
  {
    id: 'update-2026-03-16',
    title: 'Envio de Campanhas Teste',
    message: 'Agora você pode enviar campanhas de teste antes de disparar para toda a audiência.',
    icon: 'PlayCircleIcon',
    variant: 'light-info',
    date: '2026-03-16',
    details: [
      'Envio de teste para validar antes do disparo',
      'Seleção de templates com botões fixos',
      'Componente global de seleção de templates',
      'Novo template de retorno disponível',
    ],
  },
  {
    id: 'update-2026-03-13',
    title: 'Rascunho e Edição de Campanhas',
    message: 'Salve campanhas como rascunho e edite campanhas já criadas a qualquer momento.',
    icon: 'EditIcon',
    variant: 'light-warning',
    date: '2026-03-13',
    details: [
      'Salvar audiência e campanha como rascunho',
      'Edição de campanhas existentes',
      'Exclusão de audiência na criação de novas',
      'Botão para selecionar todas as opções',
    ],
  },
  {
    id: 'update-2026-03-11',
    title: 'Campanhas Segmentadas Step-by-Step',
    message: 'Criação de campanhas segmentadas agora com fluxo passo a passo e ajustes de usabilidade.',
    icon: 'TargetIcon',
    variant: 'light-primary',
    date: '2026-03-11',
    details: [
      'Fluxo simplificado de criação',
      'Botão para criar template se não existir',
      'Correções de bugs nas campanhas segmentadas',
    ],
  },
  {
    id: 'update-2026-03-10',
    title: 'Variável de Cashback',
    message: 'Adicionada a variável de cashback para uso em templates e campanhas.',
    icon: 'DollarSignIcon',
    variant: 'light-success',
    date: '2026-03-10',
  },
]

export default {
  name: 'SidebarUserProfile',
  components: {
    BAvatar,
    BButton,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BModal,
    BBadge,
  },
  props: {
    isCollapsed: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { root }) {
    const userDropdown = ref(null)
    const showNotificationsModal = ref(false)
    // const showInstabilityAlert = ref(false)
    // const showInstabilityConfirm = ref(false)
    const readNotifications = ref([])
    const { skin } = useAppConfig()

    const userData = JSON.parse(localStorage.getItem('userData')) || {
      fullName: 'Usuário',
      username: 'user',
      role: 'admin',
      avatar: null,
    }

    const displayUserName = computed(() => formatUserDisplayNameCompact(userData.fullName || userData.username || ''))

    const loadReadNotifications = () => {
      try {
        const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY)
        readNotifications.value = stored ? JSON.parse(stored) : []
      } catch {
        readNotifications.value = []
      }
    }

    const saveReadNotifications = () => {
      localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(readNotifications.value))
    }

    const notifications = computed(() => PLATFORM_NOTIFICATIONS.map(n => ({
      ...n,
      read: readNotifications.value.includes(n.id),
    })))

    const unreadCount = computed(() => notifications.value.filter(n => !n.read).length)

    const isDark = computed(() => skin.value === 'dark')

    const toggleTheme = () => {
      skin.value = isDark.value ? 'light' : 'dark'
    }

    const openNotificationsModal = () => {
      showNotificationsModal.value = true
    }

    const markAsRead = notification => {
      if (!readNotifications.value.includes(notification.id)) {
        readNotifications.value.push(notification.id)
        saveReadNotifications()
      }
    }

    const markAllAsRead = () => {
      readNotifications.value = PLATFORM_NOTIFICATIONS.map(n => n.id)
      saveReadNotifications()
    }

    // const checkInstabilityAlert = () => {
    //   const dismissed = localStorage.getItem(INSTABILITY_ALERT_KEY)
    //   if (!dismissed) {
    //     showInstabilityAlert.value = true
    //   }
    // }

    // const openInstabilityConfirm = () => {
    //   showInstabilityAlert.value = false
    //   showInstabilityConfirm.value = true
    // }

    // const cancelInstabilityConfirm = () => {
    //   showInstabilityConfirm.value = false
    //   showInstabilityAlert.value = true
    // }

    // const dismissInstabilityAlert = () => {
    //   localStorage.setItem(INSTABILITY_ALERT_KEY, 'dismissed')
    //   showInstabilityConfirm.value = false
    // }

    const formatDate = dateStr => {
      const date = new Date(dateStr)
      const now = new Date()
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'Hoje'
      if (diffDays === 1) return 'Ontem'
      if (diffDays < 7) return `${diffDays} dias atrás`

      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
      })
    }

    const logout = () => {
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)
      localStorage.removeItem('mc_acess_token')
      localStorage.removeItem('userData')
      root.$ability.update(initialAbility)
      root.$router.push({ name: 'auth-login' })
    }

    const goToMeuPlano = () => {
      root.$router.push({ name: 'meu-plano' }).catch(() => {})
    }

    const goToFormaPagamento = () => {
      root.$router.push({ name: 'forma-pagamento' }).catch(() => {})
    }

    const goToFaturas = () => {
      root.$router.push({ name: 'faturas' }).catch(() => {})
    }

    watch(() => props.isCollapsed, collapsed => {
      if (collapsed && userDropdown.value) {
        userDropdown.value.hide()
      }
    })

    // watch(
    //   () => root.$route.path,
    //   () => {
    //     checkInstabilityAlert()
    //   },
    // )

    onMounted(() => {
      loadReadNotifications()
      // checkInstabilityAlert()
    })

    return {
      userDropdown,
      userData,
      displayUserName,
      isDark,
      toggleTheme,
      logout,
      goToMeuPlano,
      goToFormaPagamento,
      goToFaturas,
      showNotificationsModal,
      // showInstabilityAlert,
      // showInstabilityConfirm,
      notifications,
      unreadCount,
      openNotificationsModal,
      markAsRead,
      markAllAsRead,
      formatDate,
      // openInstabilityConfirm,
      // cancelInstabilityConfirm,
      // dismissInstabilityAlert,
    }
  },
}
</script>

  <style lang="scss" scoped>
  .sidebar-user-profile {
    position: relative;
    padding: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    background: inherit;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
    margin-top: auto;

    &.collapsed {
      padding: 0.75rem 0.5rem;
    }
  }

  .user-profile-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;

    .collapsed & {
      flex-direction: column;
      gap: 0.75rem;

      // Footer retraído: notificação → tema → avatar (último)
      .theme-toggle-collapsed {
        order: 1;
      }

      .sidebar-footer-user-dropdown {
        order: 2;
      }
    }
  }

  .user-dropdown-toggle {
    flex: 1;
    border: none !important;
    box-shadow: none !important;

    &:hover,
    &:focus,
    &:active {
      box-shadow: none !important;
      background: transparent !important;
    }
  }

  .user-info-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    min-width: 0;
    padding: 0.5rem;
    border-radius: 8px;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      background: rgba(115, 103, 240, 0.08);
    }

    .collapsed & {
      flex-direction: column;
      gap: 0.5rem;
      width: 100%;
      padding: 0.25rem;
    }
  }

  .user-avatar {
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  .chevron-icon {
    margin-left: auto;
    flex-shrink: 0;
    opacity: 0.6;
    transition: transform 0.2s ease;

    .show & {
      transform: rotate(180deg);
    }
  }

  .user-details {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
  }

  .user-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: inherit;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
  }

  .user-role {
    font-size: 0.75rem;
    color: #6e6b7b;
    text-transform: capitalize;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .theme-toggle-wrapper,
  .notification-toggle-wrapper {
    flex-shrink: 0;
  }

  .theme-toggle-collapsed {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .theme-toggle-btn,
  .notification-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    transition: all 0.2s ease;
    position: relative;

    &:hover {
      background: rgba(115, 103, 240, 0.12) !important;
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .notification-badge {
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    font-size: 10px;
    font-weight: 600;
    line-height: 18px;
    text-align: center;
    color: #fff;
    background: linear-gradient(135deg, #ea5455 0%, #f08182 100%);
    border-radius: 10px;
    box-shadow: 0 2px 6px rgba(234, 84, 85, 0.4);
  }

  // Dropdown menu styles
  ::v-deep .user-dropdown-menu {
    min-width: 180px;

    .dropdown-item {
      display: flex;
      align-items: center;
      padding: 0.75rem 1rem;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(115, 103, 240, 0.12);
      }
    }
  }

  // Dark mode styles
  .dark-layout {
    .sidebar-user-profile {
      border-top-color: rgba(255, 255, 255, 0.08);
    }

    .user-role {
      color: #b4b7bd;
    }

    .user-info-wrapper:hover {
      background: rgba(115, 103, 240, 0.15);
    }

    .theme-toggle-btn:hover {
      background: rgba(115, 103, 240, 0.2) !important;
    }

    ::v-deep .user-dropdown-menu .dropdown-item:hover {
      background: rgba(115, 103, 240, 0.2);
    }
  }

  // Responsividade
  @media (max-width: 1199.98px) {
    .sidebar-user-profile {
      padding: 0.75rem;
    }

    .user-name {
      font-size: 0.9rem;
    }

    .user-role {
      font-size: 0.7rem;
    }
  }

  // Quando o menu está expandido em hover (collapsed state)
  .main-menu:not(.expanded) {
    .sidebar-user-profile {
      .user-details {
        opacity: 0;
        width: 0;
        overflow: hidden;
      }
    }
  }

  // Animação quando expande
  .main-menu.expanded {
    .user-details {
      animation: fadeInLeft 0.3s ease;
    }
  }

  @keyframes fadeInLeft {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  // Estilos do Modal de Notificações
  .notifications-container {
    max-height: 60vh;
    overflow-y: auto;
  }

  .notification-item {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    margin-bottom: 0.75rem;
    border-radius: 10px;
    background: rgba(115, 103, 240, 0.04);
    border: 1px solid transparent;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      background: rgba(115, 103, 240, 0.08);
      border-color: rgba(115, 103, 240, 0.15);
    }

    &:last-child {
      margin-bottom: 0;
    }

    &.unread {
      background: rgba(115, 103, 240, 0.08);
      border-left: 3px solid #7367f0;

      .notification-title {
        font-weight: 700;
      }
    }
  }

  .notification-icon {
    flex-shrink: 0;
  }

  .notification-content {
    flex: 1;
    min-width: 0;
  }

  .notification-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
  }

  .notification-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #5e5873;
  }

  .notification-date {
    flex-shrink: 0;
    font-size: 0.75rem;
  }

  .notification-text {
    font-size: 0.875rem;
    color: #6e6b7b;
    line-height: 1.5;
  }

  .notification-details {
    font-size: 0.8rem;

    li {
      margin-bottom: 0.25rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .notifications-footer {
    display: flex;
    justify-content: center;
  }

  // Dark mode para notificações
  .dark-layout {
    .notification-item {
      background: rgba(115, 103, 240, 0.08);

      &:hover {
        background: rgba(115, 103, 240, 0.15);
      }

      &.unread {
        background: rgba(115, 103, 240, 0.12);
      }
    }

    .notification-title {
      color: #d0d2d6;
    }

    .notification-text {
      color: #b4b7bd;
    }
  }

  /*
  // Modal de instabilidade (desativado)
  .instability-alert-content {
    text-align: center;
    padding: 1rem 0.5rem;
  }
  .alert-icon-wrapper {
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
  }
  .alert-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #ea5455;
  }
  .alert-message {
    font-size: 0.95rem;
    line-height: 1.6;
  }
  .alert-details {
    background: rgba(234, 84, 85, 0.08);
    border-radius: 8px;
    padding: 1rem;
    text-align: left;
  }
  .alert-detail-item {
    display: flex;
    align-items: flex-start;
    font-size: 0.875rem;
    color: #5e5873;
    margin-bottom: 0.5rem;
    &:last-child {
      margin-bottom: 0;
    }
    svg {
      margin-top: 2px;
      flex-shrink: 0;
    }
  }
  .alert-footer-text {
    font-style: italic;
  }
  .dark-layout {
    .alert-details {
      background: rgba(234, 84, 85, 0.12);
    }
    .alert-detail-item {
      color: #b4b7bd;
    }
  }
  */
  </style>
